import React, { useCallback, useEffect, useRef, useState } from 'react';
import styles from '../Detail/ScormTrainingContent.module.scss';
import Loading from '../../../components/loading';
import { hostName, isLocal } from '../../../constants/apiEnv';

function ScormContent(props) {
  const { history, match: { params } } = props;
  const iframeRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [scormUrl, setScormUrl] = useState('');
  const [scormData, setScormData] = useState({});
  const [scormVersion, setScormVersion] = useState('');
  const [trainingDraft, setTrainingDraft] = useState({});
  const completionTriggered = useRef(false);

  const getScormURL = async () => {
    const url = hostName.slice(6);
    const publicUrl = isLocal ? process.env.REACT_APP_BACKEND_PUBLICHOST : `https://apis.${params?.backendId || 'us'}${url}/public`;
    setLoading(true);
    await fetch(`${publicUrl}/lms/public_scorm_file_content/${params.airportCode.toUpperCase()}/${params.trainingId}/`)
      .then(response => response.json())
      .then((data) => {
        if (data?.url && data?.url !== scormUrl) {
          setScormUrl(data?.url);
          const version = data?.scorm_version?.toString();
          setScormVersion(version?.includes('2004') ? '2004' : version);
          setLoading(false);
        }
      });

    // manage public training draft information
    await fetch(`${publicUrl}/lms/public_training_draft/${params.airportCode.toUpperCase()}/${params.trainingId}/`)
      .then(response => response.json())
      .then((draftData) => {
        setTrainingDraft(draftData);
        const cmiKeys = Object.keys(draftData?.tracking_info || {})
          .filter(key => key.startsWith('cmi') && draftData?.tracking_info[key] != null);
        const cmiObj = cmiKeys.reduce((acc, key) => ({ ...acc, [key]: draftData.tracking_info[key] }), {});
        setScormData(cmiObj);
      });
  };

  const saveTrainingDraft = useCallback(async (payload) => {
    const url = hostName.slice(6);
    const publicUrl = isLocal ? process.env.REACT_APP_BACKEND_PUBLICHOST : `https://apis.${params?.backendId || 'us'}${url}/public`;
    setLoading(true);
    try {
      const res = await fetch(`${publicUrl}/lms/public_training_draft/${params.airportCode.toUpperCase()}/${params.trainingId}/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        throw new Error(`Error: ${res.statusText}`);
      }

      const data = await res.json();
      setTrainingDraft(data);
    } catch (err) {
      // nothing
    } finally {
      setLoading(false);
    }
  }, [params]);

  const completePublicTraining = useCallback(async (id, payload) => {
    const url = hostName.slice(6);
    const publicUrl = isLocal ? process.env.REACT_APP_BACKEND_PUBLICHOST : `https://apis.${params?.backendId || 'us'}${url}/public`;
    setLoading(true);
    try {
      const res = await fetch(`${publicUrl}/lms/public_complete_training/${params.airportCode.toUpperCase()}/${params.trainingId}/${id}/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        throw new Error(`Error: ${res.statusText}`);
      }
      history.push(`/lms/view/training/${params?.backendId}/${params.airportCode.toUpperCase()}/${params?.trainingId}`);
    } catch (err) {
      // nothing
    } finally {
      setLoading(false);
    }
  }, [history, params]);

  useEffect(() => {
    if (scormUrl === '') {
      getScormURL();
    }
  });

  useEffect(() => {
    if (trainingDraft.status === 'completed') {
      history.push(`/lms/view/training/${params?.backendId}/${params.airportCode.toUpperCase()}/${params?.trainingId}`);
    }
  }, [trainingDraft, history, params]);

  const handleIframeLoad = useCallback(() => {
    const iframe = iframeRef.current;
    if (iframe && iframe.contentWindow) {
      iframe.contentWindow.postMessage({
        type: 'INITIALIZE_SCORM',
        data: scormData
      }, '*');
    }
  }, [scormData]);

  const isScormVersion = (version, type) => ((version === '1.1' || version === '1.2')
    ? type === 'cmi.core.lesson_status' : type === 'cmi.completion_status' || type === 'cmi.success_status' || type === 'cmi.location');

  const handleMessage = useCallback((event) => {
    const status = ['passed', 'completed', 'failed'];
    if (event.data.type === 'SCORM_SET_VALUE') {
      const { element, value } = event.data.data;
      const payload = {
        tracking_info: { [element]: value }
      };
      if (completionTriggered.current) return;
      if (scormVersion === '2004' && scormData['cmi.location'] !== undefined) {
        payload.level_completion = { 'cmi.location': scormData['cmi.location'] };
      }

      if (value && !['cmi.core.session_time', 'cmi.session_time'].includes(element)) {
        updateScormData(element, value);
        saveTrainingDraft(payload);
      } else if (value) {
        updateScormData(element, value);
        saveTrainingDraft(payload);
      }
      if (!completionTriggered.current) {
        if (isScormVersion(scormVersion, element) && status.includes(value) && trainingDraft) {
          const data = { status: value === 'failed' ? 'fail' : value };
          completionTriggered.current = true;
          completePublicTraining(trainingDraft.id, data);
        } else if (isScormVersion(scormVersion, element) && ['finish'].includes(value) && trainingDraft) {
          const data = { status: value === 'passed' };
          completePublicTraining(trainingDraft.id, data);
        }
      }
    }
  }, [completePublicTraining, saveTrainingDraft, scormData, scormVersion, trainingDraft]);

  useEffect(() => {
    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [handleMessage]);

  const updateScormData = (key, value) => {
    setScormData(prevScormData => ({
      ...prevScormData,
      [key]: value

    }));
  };

  useEffect(() => {
    const element = document.querySelector('body');
    if (element.classList.contains('modal-open')) {
      element.classList.remove('modal-open');
    }
  }, [loading]);

  return (
    <div className={styles.scormContainer}>
      <Loading loadingStatus={loading} />
      <div className={styles.content}>
        {Object.keys(trainingDraft).length !== 0 && (
          <iframe
            ref={iframeRef}
            src={scormUrl}
            className={styles.scormContent}
            title="scorm"
            onLoad={handleIframeLoad}
          />
        )}
      </div>
    </div>
  );
}

export default ScormContent;
